import { collection, getDocs, getFirestore, onSnapshot, Unsubscribe } from 'firebase/firestore';
import { getFirebaseApp } from '../firebaseApp';

export interface CachedStaffMember {
  staffMemberId: string;
  name: string;
  contactId?: string;
  email?: string;
  phone?: string;
  resourceId?: string;
  mainMedia?: string;
  wixUserId?: string;
  contactAvatar?: string;
}

/**
 * Staff Cache Service for Frontend
 * Reads staff members from Firestore rs-roles database
 * Provides instant access to staff names and info without API calls
 */
export class StaffCacheService {
  private static instance: StaffCacheService | null = null;
  private staffCache: Map<string, CachedStaffMember> = new Map();
  private staffByContactId: Map<string, CachedStaffMember> = new Map();
  private staffByWixUserId: Map<string, CachedStaffMember> = new Map();
  private isLoaded = false;
  private loadPromise: Promise<void> | null = null;
  private unsubscribe: Unsubscribe | null = null;

  private constructor() {}

  static getInstance(): StaffCacheService {
    if (!StaffCacheService.instance) {
      StaffCacheService.instance = new StaffCacheService();
    }
    return StaffCacheService.instance;
  }

  private getFirestore() {
    return getFirestore(getFirebaseApp(), 'rs-roles');
  }

  async loadStaffCache(forceRefresh = false): Promise<void> {
    if (forceRefresh) {
      this.isLoaded = false;
      this.loadPromise = null;
    }
    if (this.loadPromise) return this.loadPromise;
    if (this.isLoaded) return Promise.resolve();
    this.loadPromise = this._loadStaffCache();
    await this.loadPromise;
    this.loadPromise = null;
  }

  private async _loadStaffCache(): Promise<void> {
    const db = this.getFirestore();
    try {
      console.log('📋 StaffCacheService: Loading staff from Firestore...');
      const startTime = Date.now();
      const staffRef = collection(db, 'staff');
      const snapshot = await getDocs(staffRef);
      this.staffCache.clear();
      this.staffByContactId.clear();
      this.staffByWixUserId.clear();
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        const staffId = data.staffMemberId || doc.id;
        const staffMember: CachedStaffMember = {
          staffMemberId: staffId,
          name: data.name,
          contactId: data.contactId,
          email: data.email,
          phone: data.phone,
          resourceId: data.resourceId,
          mainMedia: data.mainMedia,
          wixUserId: data.wixUserId,
          contactAvatar: data.contactAvatar,
        };
        this.staffCache.set(staffId, staffMember);
        if (data.contactId) this.staffByContactId.set(data.contactId, staffMember);
        if (data.wixUserId) this.staffByWixUserId.set(data.wixUserId, staffMember);
      });
      this.isLoaded = true;
      const loadTime = Date.now() - startTime;
      console.log(`✅ StaffCacheService: Loaded ${this.staffCache.size} staff members in ${loadTime}ms`);
      const staffList = Array.from(this.staffCache.values()).map(s => ({
        name: s.name, staffMemberId: s.staffMemberId, contactId: s.contactId, wixUserId: s.wixUserId
      }));
      console.log(`📋 Staff cache contents:`, staffList);
    } catch (error) {
      console.error('❌ StaffCacheService: Error loading staff cache:', error);
      this.isLoaded = false;
      throw error;
    }
  }

  subscribeToStaffUpdates(): void {
    if (this.unsubscribe) {
      console.log('⚠️ StaffCacheService: Already subscribed to staff updates');
      return;
    }
    const db = this.getFirestore();
    const staffRef = collection(db, 'staff');
    this.unsubscribe = onSnapshot(staffRef, (snapshot) => {
      snapshot.docChanges().forEach(change => {
        const data = change.doc.data();
        const staffId = data.staffMemberId || change.doc.id;
        if (change.type === 'added' || change.type === 'modified') {
          const staffMember: CachedStaffMember = {
            staffMemberId: staffId,
            name: data.name,
            contactId: data.contactId,
            email: data.email,
            phone: data.phone,
            resourceId: data.resourceId,
            mainMedia: data.mainMedia,
            wixUserId: data.wixUserId,
            contactAvatar: data.contactAvatar,
          };
          this.staffCache.set(staffId, staffMember);
          if (data.contactId) this.staffByContactId.set(data.contactId, staffMember);
          if (data.wixUserId) this.staffByWixUserId.set(data.wixUserId, staffMember);
          console.log(`✅ StaffCacheService: Updated staff ${data.name} (${staffId})`);
        } else if (change.type === 'removed') {
          const removedStaff = this.staffCache.get(staffId);
          if (removedStaff) {
            this.staffCache.delete(staffId);
            if (removedStaff.contactId) this.staffByContactId.delete(removedStaff.contactId);
            if (removedStaff.wixUserId) this.staffByWixUserId.delete(removedStaff.wixUserId);
            console.log(`✅ StaffCacheService: Removed staff ${staffId}`);
          }
        }
      });
    }, (error) => {
      console.error('❌ StaffCacheService: Error in staff subscription:', error);
    });
    console.log('✅ StaffCacheService: Subscribed to staff updates');
  }

  unsubscribeFromStaffUpdates(): void {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
      console.log('✅ StaffCacheService: Unsubscribed from staff updates');
    }
  }

  getStaffById(staffMemberId: string): CachedStaffMember | undefined {
    return this.staffCache.get(staffMemberId);
  }

  getStaffByContactId(contactId: string): CachedStaffMember | undefined {
    return this.staffByContactId.get(contactId);
  }

  getStaffByWixUserId(wixUserId: string): CachedStaffMember | undefined {
    return this.staffByWixUserId.get(wixUserId);
  }

  getStaffByName(name: string): CachedStaffMember | undefined {
    for (const staff of this.staffCache.values()) {
      if (staff.name === name) return staff;
    }
    // No logging here: this is called per message (a miss is normal for non-staff senders).
    return undefined;
  }

  getStaffName(identifier: { staffMemberId?: string; contactId?: string }): string | undefined {
    if (identifier.staffMemberId) {
      const staff = this.getStaffById(identifier.staffMemberId);
      if (staff) return staff.name;
    }
    if (identifier.contactId) {
      const staff = this.getStaffByContactId(identifier.contactId);
      if (staff) return staff.name;
    }
    return undefined;
  }

  getAllStaff(): CachedStaffMember[] {
    return Array.from(this.staffCache.values());
  }

  isStaffCacheLoaded(): boolean {
    return this.isLoaded;
  }

  getStaffCacheSize(): number {
    return this.staffCache.size;
  }
}
